import Delete from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Chip from '@mui/material/Chip';
import Grid2 from '@mui/material/Grid2';
import Popover from '@mui/material/Popover';
import TextField from "@mui/material/TextField";
import { GridColDef, GridRenderCellParams, GridRenderEditCellParams, useGridApiContext } from "@mui/x-data-grid-pro";
import { DatePicker } from "@mui/x-date-pickers";
import { DateTime } from 'luxon';
import React, { useEffect, useMemo } from "react";

export const GridEditDateCell = (props: GridRenderEditCellParams) => {

  const { id, field, value } = props;

  const apiRef = useGridApiContext();

  const handleChange = (newValue: any) => {
    apiRef.current.setEditCellValue({ id, field, value: (newValue as DateTime).toFormat('yyyy-LL-dd') });
  };

  return <DatePicker
    value={value ? DateTime.fromFormat(value, 'yyyy-LL-dd') : null}
    renderInput={(params) => <TextField {...params} />}
    onChange={handleChange}
  />;
};

export const dateColumnType: Omit<GridColDef<any>, 'field'> = {
  renderEditCell: (params) => <GridEditDateCell {...params} />,
};

const GridRelanceCell = (props: GridRenderCellParams & { editing?: boolean; }) => {

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const divEl = React.useRef<HTMLDivElement | null>(null);

  const handleClose = () => {
    setAnchorEl(null);
    if (props.cellMode == 'edit') {
      apiRef.current.stopCellEditMode({ id, field });
    }
  };

  useEffect(() => {
    switch (props.cellMode) {
      case 'view':
        break;
      case 'edit':
        if (divEl.current) {
          return setAnchorEl(divEl.current);
        };
        break;
    }
    return setAnchorEl(null);
  }, [props.cellMode, divEl]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    switch (props.cellMode) {
      case 'view':
        apiRef.current.startCellEditMode({ id, field });
    }
  };

  const open = Boolean(anchorEl);

  const now = DateTime.now().set({ hour: 0, second: 0, minute: 0, millisecond: 0 });

  const { id, field, value } = props;

  const apiRef = useGridApiContext();

  const handleChange = (newValue: any) => {
    apiRef.current.setEditCellValue({ id, field, value: (newValue as DateTime).toFormat('yyyy-LL-dd') });
    apiRef.current.stopCellEditMode({ id, field });
  };

  const dateValue: DateTime | null = useMemo(() => {
    if (value && value != "") {
      return DateTime.fromFormat(value, 'yyyy-LL-dd');
    } else {
      return null;
    }
  }, [value]);

  const dayDiff = useMemo(() => {
    if (dateValue) {
      const diff = dateValue.diff(now, ['days', 'hours']);
      return diff.days;
    } else {
      return null;
    }
  }, [dateValue]);

  const day = useMemo(() => {
    if (dateValue) {
      return dateValue.weekday; // 1 monday 7 sunday
    } else {
      return 0;
    }
  }, [dateValue]);

  const update = (date: DateTime | null) => {
    let p: any = {};
    p[props.field] = date?.toFormat('yyyy-LL-dd') ?? null;
    //apiRef.current.updateRows([{ ...props.row, ...p }]);
    //apiRef.current.startCellEditMode({ field, id });
    apiRef.current.setEditCellValue({ id, field, value: date?.toFormat('yyyy-LL-dd') ?? null });
    //apiRef.current.stopCellEditMode({ id, field });
  };

  const dayString = (day: number) => {
    switch (day) {
      case 1:
        return 'Lundi';
      case 2:
        return 'Mardi';
      case 3:
        return 'Mercredi';
      case 4:
        return 'Jeudi';
      case 5:
        return 'Vendredi';
      case 6:
        return 'Samedi';
      case 7:
        return 'Dimanche';
    }
  };

  const pillContent = () => {
    let pre = '';
    if (dayDiff !== null && dayDiff > 0) {
      pre = dayDiff + 'J - ';
    }
    if (dayDiff !== null) {
      switch (dayDiff) {
        case -2:
          return 'Avant-hier';
        case -1:
          return 'Hier';
        case 0:
          return pre + 'Aujourd\'hui';
        case 1:
          return pre + 'Demain';
        default:
          if (dayDiff < 0 && dayDiff >= -7) {
            return dayString(day) + ' dernier';
          } else if (dayDiff > 0 && dayDiff <= 6) {
            return pre + dayString(day);
          } else {
            return pre + dateValue?.setLocale('fr').toFormat('ccc dd MMMM yyyy');
          }
      }
    }
  };

  return <>
    <div ref={divEl}>
      <Chip onClick={handleClick} title={dateValue?.setLocale('fr').toFormat('dd MMMM yyyy')} size='small' color={dayDiff !== null && dayDiff > 0 ? 'success' : (dayDiff === 0 ? 'info' : 'default')} label={dateValue ? pillContent() : 'Aucune relance'} />
    </div>
    {<Popover
      open={open}
      onClose={handleClose}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}>
      <Grid2 container spacing={2} margin={2}>
        <Grid2 size={12}>
          <ButtonGroup aria-label="Basic button group">
            <Button variant={dayDiff == 1 ? 'contained' : 'outlined'} onClick={() => {
              update(null);
            }}><Delete /></Button>
            <Button variant={dayDiff == 1 ? 'contained' : 'outlined'} onClick={() => {
              update(now.plus({ day: 1 }));
            }}>Demain</Button>
            <Button variant={(day == 5 && dayDiff !== null && dayDiff >= 0 && dayDiff <= 7) ? 'contained' : 'outlined'} onClick={() => {
              let currDay = now.weekday;
              let add = 0;
              if (currDay == 5) {
                currDay++;
                add++;
              }
              while (currDay != 5) {
                add++;
                currDay++;
                if (currDay > 7) currDay = 1;
              }
              update(now.plus({ day: add }));
            }}>Ven.</Button>
            <Button variant={(day == 1 && dayDiff !== null && dayDiff >= 0 && dayDiff <= 7) ? 'contained' : 'outlined'} onClick={() => {
              let currDay = now.weekday;
              let add = 0;
              if (currDay == 1) {
                currDay++;
                add++;
              }
              while (currDay != 1) {
                add++;
                currDay++;
                if (currDay > 7) currDay = 1;
              }
              update(now.plus({ day: add }));
            }}>Lun.</Button>
          </ButtonGroup>
        </Grid2>
        <Grid2 size={12}>
          <ButtonGroup aria-label="Basic button group">
            <Button variant='text'>Dans</Button>
            <Button variant={dayDiff == 2 ? 'contained' : 'outlined'} onClick={() => {
              update(now.plus({ day: 2 }));
            }}>2</Button>
            <Button variant={dayDiff == 3 ? 'contained' : 'outlined'} onClick={() => {
              update(now.plus({ day: 3 }));
            }}>3</Button>
            <Button variant={dayDiff == 4 ? 'contained' : 'outlined'} onClick={() => {
              update(now.plus({ day: 4 }));
            }}>4</Button>
            <Button variant={dayDiff == 5 ? 'contained' : 'outlined'} onClick={() => {
              update(now.plus({ day: 5 }));
            }}>5</Button>
            <Button variant='text'>Jours</Button>
          </ButtonGroup>
        </Grid2>
        <Grid2 size={12}>
          <DatePicker
            value={value ? DateTime.fromFormat(value, 'yyyy-LL-dd') : null}
            renderInput={(params) => <TextField {...params} />}
            onChange={handleChange}
          />
        </Grid2>
      </Grid2>
    </Popover>}
  </>
    ;
};

export const relaceColumnType: Omit<GridColDef<any>, 'field'> = {
  renderCell: (params) => <GridRelanceCell {...params} />,
  renderEditCell: (params) => <GridRelanceCell {...params} />,
};
